import { useMemo } from "react";
import styles from "../aic.module.css";
import { classMerge } from "../utility/classMerge";
import { getBaseId } from "./functions/getBaseId";
import {
  ADD_COLUMN_SPAN,
  AitHeaderCellData,
  AitLocationHeaderCell,
  REMOVE_COLUMN_SPAN,
} from "./functions/interface";
import { trCanAddColSpan } from "./functions/trAddColSpan";
import { trGetCellFromLocation } from "./functions/trGetCellFromLocation";
import { trCanRemoveColSpan } from "./functions/trRemoveColSpan";
import { useTableContext } from "./TableContextProvider";

interface AitCellColSpansProps {
  location: AitLocationHeaderCell;
}

/**
 * Controls for adding or removing a header cell's column span.
 *
 * @template T - Cell payload type
 * @param props - Component props
 * @param props.location - The header cell location
 */
export const AitCellColSpans = <T,>({ location }: AitCellColSpansProps) => {
  const { state, dispatch } = useTableContext<T>();
  const originalHeaderData = state?.originalTableData.headerData;
  const thisCell: AitHeaderCellData<T> | undefined = state?.processedTableData
    ? trGetCellFromLocation(state.processedTableData, location)
    : undefined;
  const id = getBaseId(location, state?.id);

  const changeColSpan: {
    canAdd: boolean;
    canRemove: boolean;
  } = useMemo(() => {
    if (originalHeaderData && thisCell) {
      const canAdd = trCanAddColSpan(
        originalHeaderData,
        location,
        thisCell.colSpan + 1,
        state?.originalTableData.rowHeaderColumns ??
          // istanbul ignore next
          0,
      );
      const canRemove = trCanRemoveColSpan(originalHeaderData, location, thisCell.colSpan - 1);
      return {
        canAdd: canAdd.canAdd,
        canRemove: canRemove.canRemove,
      };
    }
    return { canAdd: false, canRemove: false };
  }, [location, originalHeaderData, state?.originalTableData.rowHeaderColumns, thisCell]);

  return !thisCell ? null : (
    <div className={styles.aiwBodyRow}>
      <div className={styles.aioLabel}>Column span: </div>
      <div className={styles.aioRoValue}>{thisCell.repeatColSpan ?? thisCell.colSpan}</div>
      <div className={classMerge([styles.aioxButtonHolder, styles.padding2])}>
        {changeColSpan.canAdd !== false ? (
          <div
            id={`${id}-add-colspan`}
            className={classMerge([styles.aioxButton, styles.aioxPlus])}
            title="Add column span"
            aria-label="Add column span"
            onClick={() =>
              dispatch({
                operation: ADD_COLUMN_SPAN,
                location,
                newSpan: thisCell.colSpan + 1,
              })
            }
          />
        ) : (
          <div className={styles.aioxButton} />
        )}
        {changeColSpan.canRemove !== false && (
          <div
            id={`${id}-remove-colspan`}
            className={classMerge([styles.aioxButton, styles.aioxMinus])}
            title="Remove column span"
            aria-label="Remove column span"
            onClick={() =>
              dispatch({
                operation: REMOVE_COLUMN_SPAN,
                location,
                newSpan: thisCell.colSpan - 1,
              })
            }
          />
        )}
      </div>
    </div>
  );
};

AitCellColSpans.displayName = "AitCellColSpans";
